import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { filter } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { A11yModule } from '@angular/cdk/a11y';
import { NGXLogger } from 'ngx-logger';
import { DynamicFormPotentialAnswer, DynamicFormQuestion } from '@openapi';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DynamicFormQuestionCompletenessService } from '@pe/pe-dynamic-forms';
import { EncompassFormsErrorComponent } from '../encompass-forms-error/encompass-forms-error.component';

export const MaxLengthPlaceHolder = 60;
export const MaxLengthPatientPrompt = 500;
export const DefaultPdfMaxSizeMb = 2;

export interface EditDialogData {
  title: string;
  questions: DynamicFormQuestion[];
}

export interface EditDialogResult {
  questions: DynamicFormQuestion[];
  pendingPdfFiles: Map<string, File>;
}

@Component({
  selector: 'conn-edit-dialog',
  imports: [
    A11yModule,
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    EncompassFormsErrorComponent,
  ],
  templateUrl: './edit-dialog.component.html',
  styleUrls: ['./edit-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class EditDialogComponent implements OnInit {
  private dialogRef = inject<MatDialogRef<EditDialogComponent>>(MatDialogRef);
  private data = inject<EditDialogData>(MAT_DIALOG_DATA);
  private logger = inject(NGXLogger);
  private destroyRef = inject(DestroyRef);
  readonly questionCompletenessService = inject(
    DynamicFormQuestionCompletenessService,
  );

  readonly maxLengthPlaceHolder = MaxLengthPlaceHolder;
  readonly maxLengthPatientPrompt = MaxLengthPatientPrompt;

  title: string = this.data.title;
  showAll = true;

  private originalQuestions: DynamicFormQuestion[];

  basicInputTypes = new Set([
    'ALPHA',
    'DATE',
    'EMAIL',
    'INPUT',
    'NUMERIC',
    'PHONE',
    'DROPDOWN',
  ]);
  checkboxTypes = new Set(['CHECKBOX', 'MULTI_CHECKBOX', 'RADIO']);
  textTypes = new Set(['TEXT', 'TITLE']);

  readonly elementTypeLabels: Record<string, string> = {
    TEXT: 'Description',
    TITLE: 'Title',
    IMAGE: 'Image',
    PDF: 'PDF',
  };

  workingQuestions: DynamicFormQuestion[];

  pendingPdfFiles = new Map<string, File>();

  readonly genericIncompleteQuestionMessage = 'Current question is incomplete.';

  /**
   * Debug column feature.
   * Set showDebugToggle = true to reveal the footer toggle.
   * Toggle state persists in localStorage under 'debugFormsAdmin'.
   * The column shows only when both showDebugToggle and debugFormsAdmin are true.
   * Search for "debug" in this component to remove the feature.
   */
  showDebugToggle = false;
  debugFormsAdmin = localStorage.getItem('debugFormsAdmin') === 'true';

  onDebugToggleChange(): void {
    localStorage.setItem('debugFormsAdmin', String(this.debugFormsAdmin));
  }

  getPdfMaxSizeMb(question: DynamicFormQuestion): number {
    const value = question.potentialAnswers?.find(
      (a) => a.label === 'fileSize',
    )?.value;
    const parsed = Number(value);
    return parsed > 0 ? parsed : DefaultPdfMaxSizeMb;
  }

  getPdfFileName(question: DynamicFormQuestion): string {
    const fromAnswers = question.potentialAnswers?.find(
      (a) => a.label === 'fileName',
    )?.value;
    return fromAnswers || question.selectedValue || '';
  }

  onUploadPdfClick(
    question: DynamicFormQuestion,
    fileInput: HTMLInputElement,
  ): void {
    fileInput.value = '';
    fileInput.click();
  }

  onPdfFileSelected(event: Event, question: DynamicFormQuestion): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.pendingPdfFiles.set(question.id, file);
    question.selectedValue = file.name;

    const fileNameAnswer = question.potentialAnswers?.find(
      (a) => a.label === 'fileName',
    );
    if (fileNameAnswer) {
      fileNameAnswer.value = file.name;
    } else if (question.potentialAnswers) {
      question.potentialAnswers.push({ label: 'fileName', value: file.name });
    } else {
      question.potentialAnswers = [{ label: 'fileName', value: file.name }];
    }
  }

  onShowAllToggleChange(): void {
    this.workingQuestions.forEach((question) => {
      question.show = this.showAll;
      if (question.potentialAnswers) {
        question?.potentialAnswers.forEach((answer) => {
          answer.show = this.showAll;
        });
      }
    });
  }

  ngOnInit() {
    this.logger.info(
      `[EditDialog] Opened with questions:`,
      this.originalQuestions,
    );
    this.originalQuestions = this.cloneQuestions(this.data.questions);
    this.workingQuestions = this.cloneQuestions(this.originalQuestions);
    this.setShowAllState();

    this.dialogRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.hasChanges()) this.dialogRef.close();
      });

    this.dialogRef
      .keydownEvents()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((e) => e.key === 'Escape'),
      )
      .subscribe(() => {
        if (!this.hasChanges()) this.dialogRef.close();
      });
  }

  close(): void {
    if (
      this.hasChanges() &&
      !window.confirm(
        'Are you sure you want to cancel? Any unsaved changes will be lost.',
      )
    ) {
      return;
    }
    this.dialogRef.close();
  }

  reset(): void {
    if (!this.hasChanges()) {
      return;
    }
    if (
      window.confirm(
        'Are you sure you want to reset? All changes will be undone.',
      )
    ) {
      this.workingQuestions = this.cloneQuestions(this.originalQuestions);
      this.pendingPdfFiles.clear();
      this.setShowAllState();
    }
  }

  save(): void {
    this.logChanges();
    const result: EditDialogResult = {
      questions: this.workingQuestions,
      pendingPdfFiles: this.pendingPdfFiles,
    };
    this.dialogRef.close(result);
  }

  private logChanges(): void {
    this.workingQuestions.forEach((working, i) => {
      const original = this.originalQuestions[i];

      (Object.keys(working) as (keyof DynamicFormQuestion)[]).forEach((key) => {
        if (key === 'potentialAnswers') return;
        const from = original[key];
        const to = working[key];
        if (JSON.stringify(from) !== JSON.stringify(to)) {
          this.logger.info(
            `[EditDialog] "${working.id}" ${key}: "${from}" → "${to}"`,
          );
        }
      });

      const workingAnswers = working.potentialAnswers;
      const originalAnswers = original.potentialAnswers;
      workingAnswers?.forEach((answer, j) => {
        const origAnswer = originalAnswers?.[j];
        (Object.keys(answer) as (keyof DynamicFormPotentialAnswer)[]).forEach(
          (key) => {
            const from = origAnswer?.[key];
            const to = answer[key];
            if (JSON.stringify(from) !== JSON.stringify(to)) {
              this.logger.info(
                `[EditDialog] "${working.id}" answer "${answer.value}" ${key}: "${from}" → "${to}"`,
              );
            }
          },
        );
      });
    });
  }

  private cloneQuestions(source: DynamicFormQuestion[]): DynamicFormQuestion[] {
    const questions: DynamicFormQuestion[] = structuredClone(source);
    questions.forEach((q) => {
      q.show = q.show ?? true;
      if (q.placeHolder && q.placeHolder.length > MaxLengthPlaceHolder) {
        q.placeHolder = q.placeHolder.substring(0, MaxLengthPlaceHolder);
      }
      if (q.patientPrompt && q.patientPrompt.length > MaxLengthPatientPrompt) {
        q.patientPrompt = q.patientPrompt.substring(0, MaxLengthPatientPrompt);
      }
      q.potentialAnswers?.forEach((a) => {
        a.show = a.show ?? true;
      });
    });
    return questions;
  }

  private hasChanges(): boolean {
    return (
      this.pendingPdfFiles.size > 0 ||
      JSON.stringify(this.workingQuestions) !==
        JSON.stringify(this.originalQuestions)
    );
  }

  setShowAllState(): void {
    const hasHiddenQuestionOrAnswer = this.workingQuestions.some((question) => {
      if (question.show === false && question.required === false) {
        return true;
      }

      const answers = question.potentialAnswers;
      return answers?.some((answer) => answer.show === false) ?? false;
    });

    this.showAll = !hasHiddenQuestionOrAnswer;
  }
}
